/**
 * Hardware Control Service client (via the backend proxy).
 *
 * Control calls are authorized by the session cookie — the backend checks the
 * signed-in user's role and writes the audit log against their account.
 */

const JSON_HEADERS: HeadersInit = { 'content-type': 'application/json' };

export interface HardwareDevice {
  name: string;
  role: string;
  driver: string;
  status: string;
  latency_ms: number | null;
  last_error: string | null;
}

export interface TrackingState {
  running: boolean;
  encoder: number;
  speed_mm_s: number;
  armed: boolean;
  in_transit: Array<{ seq: number; trigger_pulse: number; reject_pulse: number; verdict: string | null }>;
  metrics: {
    triggered: number;
    rejected: number;
    passed: number;
    missed_deadline: number;
    late_verdicts: number;
  };
  watchdog_ok: boolean;
}

export interface HardwareStatus {
  ok: boolean;
  error?: string;
  health?: { status: string; station: string; devices: number; watchdog_ok: boolean };
  devices?: HardwareDevice[];
  tracking?: TrackingState;
}

async function post(path: string): Promise<void> {
  const res = await fetch(path, { method: 'POST', headers: JSON_HEADERS });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(body.message || `Request failed: ${res.status}`);
  }
}

export const hardwareApi = {
  status: async (): Promise<HardwareStatus> => {
    const res = await fetch('/api/hardware/status');
    if (!res.ok) throw new Error('hardware status failed');
    return res.json();
  },
  conveyorStart: () => post('/api/hardware/conveyor/start'),
  conveyorStop: () => post('/api/hardware/conveyor/stop'),
  conveyorSpeed: async (mm_s: number) => {
    const res = await fetch('/api/hardware/conveyor/speed', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ mm_s }),
    });
    if (!res.ok) throw new Error('set speed failed');
  },
  rejectorArm: () => post('/api/hardware/rejector/arm'),
  rejectorDisarm: () => post('/api/hardware/rejector/disarm'),
  rejectorTest: () => post('/api/hardware/rejector/test'),
};
